import { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';
import { Agency } from '../types';

function Agencies() {
  const { user: currentUser } = useUser();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [editFormData, setEditFormData] = useState({ name: '', code: '', statuses: [] as string[], transactionTypes: [] as string[] });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingAgency, setDeletingAgency] = useState<Agency | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({ name: '', code: '', statuses: [] as string[], transactionTypes: [] as string[] });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editStatusInput, setEditStatusInput] = useState('');
  const [createStatusInput, setCreateStatusInput] = useState('');
  const [editTransactionTypeInput, setEditTransactionTypeInput] = useState('');
  const [createTransactionTypeInput, setCreateTransactionTypeInput] = useState('');

  useEffect(() => {
    fetchAgencies();
  }, [currentUser]);

  const fetchAgencies = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Check if user has admin role (can see all agencies)
      const isAdmin = currentUser.roles.some(role => role.id === 'admin-role-id');
      
      if (isAdmin) {
        // Admin can see all agencies
        const response = await api.get('/agencies');
        setAgencies(response.data);
      } else {
        // Agency admin can only see their own agency
        const isAgencyAdmin = currentUser.roles.some(role => role.id === 'agency-admin-role-id');
        if (isAgencyAdmin) {
          // For agency admin, we could show only their agency or all agencies
          // For now, let's show all agencies but this could be filtered
          const response = await api.get('/agencies');
          setAgencies(response.data);
        } else {
          // Other users shouldn't access this page, but handle gracefully
          setError('You do not have permission to view agencies.');
          setLoading(false);
          return;
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch agencies:', err);
      setError(err.response?.data?.message || 'Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  const canManageAgencies = currentUser?.roles.some(role => 
    role.id === 'admin-role-id' || role.id === 'agency-admin-role-id'
  );

  const handleEditClick = (agency: Agency) => {
    setEditingAgency(agency);
    setEditFormData({ 
      name: agency.name, 
      code: agency.code, 
      statuses: (agency as any).statuses ? JSON.parse((agency as any).statuses) : [],
      transactionTypes: (agency as any).transactionTypes ? JSON.parse((agency as any).transactionTypes) : []
    });
    setEditStatusInput('');
    setEditTransactionTypeInput('');
    setIsEditModalOpen(true);
    setUpdateError(null);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingAgency(null);
    setEditFormData({ name: '', code: '', statuses: [], transactionTypes: [] });
    setEditStatusInput('');
    setEditTransactionTypeInput('');
    setUpdateError(null);
  };

  const handleDeleteClick = (agency: Agency) => {
    setDeletingAgency(agency);
    setIsDeleteModalOpen(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingAgency(null);
    setDeleteError(null);
  };

  const handleCreateClick = () => {
    setCreateFormData({ name: '', code: '', statuses: [], transactionTypes: [] });
    setCreateStatusInput('');
    setCreateTransactionTypeInput('');
    setIsCreateModalOpen(true);
    setCreateError(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateFormData({ name: '', code: '', statuses: [], transactionTypes: [] });
    setCreateStatusInput('');
    setCreateTransactionTypeInput('');
    setCreateError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Status management functions
  const addEditStatus = () => {
    if (editStatusInput.trim() && !editFormData.statuses.includes(editStatusInput.trim())) {
      setEditFormData(prev => ({
        ...prev,
        statuses: [...prev.statuses, editStatusInput.trim()]
      }));
      setEditStatusInput('');
    }
  };

  const removeEditStatus = (statusToRemove: string) => {
    setEditFormData(prev => ({
      ...prev,
      statuses: prev.statuses.filter(status => status !== statusToRemove)
    }));
  };

  const addCreateStatus = () => {
    if (createStatusInput.trim() && !createFormData.statuses.includes(createStatusInput.trim())) {
      setCreateFormData(prev => ({
        ...prev,
        statuses: [...prev.statuses, createStatusInput.trim()]
      }));
      setCreateStatusInput('');
    }
  };

  const removeCreateStatus = (statusToRemove: string) => {
    setCreateFormData(prev => ({
      ...prev,
      statuses: prev.statuses.filter(status => status !== statusToRemove)
    }));
  };

  const handleEditStatusKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEditStatus();
    }
  };

  const handleCreateStatusKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCreateStatus();
    }
  };

  // Transaction type management functions
  const addEditTransactionType = () => {
    if (editTransactionTypeInput.trim() && !editFormData.transactionTypes.includes(editTransactionTypeInput.trim())) {
      setEditFormData(prev => ({
        ...prev,
        transactionTypes: [...prev.transactionTypes, editTransactionTypeInput.trim()]
      }));
      setEditTransactionTypeInput('');
    }
  };

  const removeEditTransactionType = (transactionTypeToRemove: string) => {
    setEditFormData(prev => ({
      ...prev,
      transactionTypes: prev.transactionTypes.filter(transactionType => transactionType !== transactionTypeToRemove)
    }));
  };

  const addCreateTransactionType = () => {
    if (createTransactionTypeInput.trim() && !createFormData.transactionTypes.includes(createTransactionTypeInput.trim())) {
      setCreateFormData(prev => ({
        ...prev,
        transactionTypes: [...prev.transactionTypes, createTransactionTypeInput.trim()]
      }));
      setCreateTransactionTypeInput('');
    }
  };

  const removeCreateTransactionType = (transactionTypeToRemove: string) => {
    setCreateFormData(prev => ({
      ...prev,
      transactionTypes: prev.transactionTypes.filter(transactionType => transactionType !== transactionTypeToRemove)
    }));
  };

  const handleEditTransactionTypeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEditTransactionType();
    }
  };

  const handleCreateTransactionTypeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCreateTransactionType();
    }
  };

  const handleUpdateAgency = async () => {
    if (!editingAgency) return;

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const response = await api.put(`/agencies/${editingAgency.id}`, {
        name: editFormData.name,
        code: editFormData.code,
        isActive: editingAgency.isActive, // Include isActive field
        statuses: JSON.stringify(editFormData.statuses),
        transactionTypes: JSON.stringify(editFormData.transactionTypes)
      });

      // Update the agencies list with the updated agency
      setAgencies(prev => prev.map(agency => 
        agency.id === editingAgency.id 
          ? { 
              ...agency, 
              ...response.data, 
              statuses: JSON.stringify(editFormData.statuses), // Ensure statuses are preserved
              updatedAt: new Date().toISOString() 
            }
          : agency
      ));

      handleCloseModal();
    } catch (err: any) {
      console.error('Failed to update agency:', err);
      setUpdateError(err.response?.data?.message || 'Failed to update agency');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAgency = async () => {
    if (!deletingAgency) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await api.delete(`/agencies/${deletingAgency.id}`);

      // Remove the agency from the list
      setAgencies(prev => prev.filter(agency => agency.id !== deletingAgency.id));

      handleCloseDeleteModal();
    } catch (err: any) {
      console.error('Failed to delete agency:', err);
      
      // Handle specific business logic errors
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete agency';
      
      if (errorMessage.includes('existing users')) {
        setDeleteError('Cannot delete this agency because it has associated users. Please reassign or remove all users from this agency first.');
      } else if (errorMessage.includes('existing transactions')) {
        setDeleteError('Cannot delete this agency because it has associated transactions. Please remove all transactions from this agency first.');
      } else {
        setDeleteError(errorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateAgency = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      // Generate a unique ID for the new agency
      const newAgencyId = `agency-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await api.post('/agencies', {
        id: newAgencyId,
        name: createFormData.name,
        code: createFormData.code,
        isActive: true,
        statuses: JSON.stringify(createFormData.statuses),
        transactionTypes: JSON.stringify(createFormData.transactionTypes)
      });

      // Add the new agency to the list
      setAgencies(prev => [response.data, ...prev]);

      handleCloseCreateModal();
    } catch (err: any) {
      console.error('Failed to create agency:', err);
      setCreateError(err.response?.data?.message || 'Failed to create agency');
    } finally {
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1>Agency Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--secondary-color)' }}>
          Loading agencies...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Agency Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--primary-color)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1>Agency Management</h1>
      
      {canManageAgencies && (
        <div style={{ marginTop: '20px' }}>
          <button 
            onClick={handleCreateClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'none',
              border: '1px solid #FC5003',
              color: '#FC5003',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '1rem',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="material-symbols-outlined small">add</span>
            Add New Agency
          </button>
        </div>
      )}

      <div style={{ marginTop: '30px' }}>
        <h2>Agencies List</h2>
        
        {agencies.length === 0 ? (
          <p style={{ color: 'var(--secondary-color)' }}>No agencies found.</p>
        ) : (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: 'var(--background-color)', 
                  borderBottom: '1px solid #e0e0e0' 
                }}>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Name
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Code
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Status
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Created
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Updated
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Allowed Statuses
                  </th>
                  {canManageAgencies && (
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: 'var(--secondary-color)'
                    }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {agencies.map((agency) => (
                  <tr key={agency.id} style={{ 
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <td style={{ padding: '12px 16px' }}>
                      {agency.name}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#e9ecef',
                        color: '#495057',
                        fontFamily: 'monospace'
                      }}>
                        {agency.code}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: agency.isActive ? '#d4edda' : '#f8d7da',
                        color: agency.isActive ? '#155724' : '#721c24'
                      }}>
                        {agency.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#6c757d' }}>
                      {new Date(agency.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#6c757d' }}>
                      {new Date(agency.updatedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {((agency as any).statuses ? JSON.parse((agency as any).statuses) : []).map((status: string, index: number) => (
                          <span
                            key={index}
                            style={{
                              padding: '2px 6px',
                              backgroundColor: '#e9ecef',
                              color: '#495057',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            {status}
                          </span>
                        ))}
                        {!((agency as any).statuses ? JSON.parse((agency as any).statuses) : []).length && (
                          <span style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                            No statuses
                          </span>
                        )}
                      </div>
                    </td>
                    {canManageAgencies && (
                      <td style={{ padding: '12px 16px' }}>
                        <button 
                          onClick={() => handleEditClick(agency)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            marginRight: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Edit agency"
                          aria-label="Edit agency"
                        >
                          <span className="material-symbols-outlined primary">edit</span>
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(agency)}
                          disabled={(agency as any)._count && (agency as any)._count.users > 0}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: (agency as any)._count && (agency as any)._count.users > 0 ? 'not-allowed' : 'pointer',
                            padding: '8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: (agency as any)._count && (agency as any)._count.users > 0 ? 0.4 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!(agency as any)._count || !(agency as any)._count.users) {
                              e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title={(agency as any)._count && (agency as any)._count.users > 0 ? "Cannot delete - has users" : "Delete agency"}
                          aria-label={(agency as any)._count && (agency as any)._count.users > 0 ? "Cannot delete - has users" : "Delete agency"}
                        >
                          <span className="material-symbols-outlined error">delete</span>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Agency Modal */}
      {isEditModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: 'var(--primary-color)',
              fontSize: '20px'
            }}>
              Edit Agency
            </h2>
            
            {updateError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {updateError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Agency Name
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter agency name"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Agency Code
              </label>
              <input
                type="text"
                name="code"
                value={editFormData.code}
                onChange={handleFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'monospace'
                }}
                placeholder="Enter agency code"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Allowed Statuses
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '8px',
                minHeight: '32px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}>
                {editFormData.statuses.map((status, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {status}
                    <button
                      type="button"
                      onClick={() => removeEditStatus(status)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        marginLeft: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '0',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={editStatusInput}
                  onChange={(e) => setEditStatusInput(e.target.value)}
                  onKeyPress={handleEditStatusKeyPress}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Type status and press Enter to add"
                />
                <button
                  type="button"
                  onClick={addEditStatus}
                  disabled={!editStatusInput.trim()}
                  style={{
                    backgroundColor: editStatusInput.trim() ? 'var(--primary-color)' : '#ccc',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: editStatusInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Allowed Transaction Types
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '8px',
                minHeight: '32px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}>
                {editFormData.transactionTypes.map((transactionType, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {transactionType}
                    <button
                      type="button"
                      onClick={() => removeEditTransactionType(transactionType)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        marginLeft: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '0',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={editTransactionTypeInput}
                  onChange={(e) => setEditTransactionTypeInput(e.target.value)}
                  onKeyPress={handleEditTransactionTypeKeyPress}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Type transaction type and press Enter to add"
                />
                <button
                  type="button"
                  onClick={addEditTransactionType}
                  disabled={!editTransactionTypeInput.trim()}
                  style={{
                    backgroundColor: editTransactionTypeInput.trim() ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: editTransactionTypeInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseModal}
                disabled={isUpdating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #015F93',
                  color: '#015F93',
                  borderRadius: '8px',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isUpdating ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleUpdateAgency}
                disabled={isUpdating || !editFormData.name.trim() || !editFormData.code.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #FC5003',
                  color: '#FC5003',
                  borderRadius: '8px',
                  cursor: isUpdating || !editFormData.name.trim() || !editFormData.code.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isUpdating || !editFormData.name.trim() || !editFormData.code.trim() ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isUpdating && editFormData.name.trim() && editFormData.code.trim()) {
                    e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-symbols-outlined small">save</span>
                {isUpdating ? 'Updating...' : 'Update Agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingAgency && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: '#dc3545',
              fontSize: '20px'
            }}>
              Confirm Delete
            </h2>
            
            {deleteError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {deleteError}
              </div>
            )}

            <p style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              color: '#333'
            }}>
              Are you sure you want to delete the agency <strong>"{deletingAgency.name}"</strong>? 
              This action cannot be undone.
            </p>

            {(deletingAgency as any)._count && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                <div style={{ marginBottom: '8px', fontWeight: '600', color: '#495057' }}>
                  Current associations:
                </div>
                <div style={{ color: '#6c757d' }}>
                  • Users: {(deletingAgency as any)._count.users}
                </div>
                <div style={{ color: '#6c757d' }}>
                  • Transactions: {(deletingAgency as any)._count.transactions}
                </div>
                {(deletingAgency as any)._count.users > 0 && (
                  <div style={{ 
                    color: '#dc3545', 
                    fontWeight: '500', 
                    marginTop: '8px',
                    fontSize: '13px'
                  }}>
                    ⚠️ This agency cannot be deleted because it has associated users.
                  </div>
                )}
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleDeleteAgency}
                disabled={isDeleting || ((deletingAgency as any)._count && (deletingAgency as any)._count.users > 0)}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: (isDeleting || ((deletingAgency as any)._count && (deletingAgency as any)._count.users > 0)) ? '#ccc' : '#dc3545',
                  opacity: (isDeleting || ((deletingAgency as any)._count && (deletingAgency as any)._count.users > 0)) ? 0.6 : 1,
                  cursor: (isDeleting || ((deletingAgency as any)._count && (deletingAgency as any)._count.users > 0)) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="material-symbols-outlined small">delete</span>
                {isDeleting ? 'Deleting...' : 
                 ((deletingAgency as any)._count && (deletingAgency as any)._count.users > 0) ? 'Cannot Delete (Has Users)' : 
                 'Delete Agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agency Modal */}
      {isCreateModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: 'var(--primary-color)',
              fontSize: '20px'
            }}>
              Create New Agency
            </h2>
            
            {createError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {createError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Agency Name
              </label>
              <input
                type="text"
                name="name"
                value={createFormData.name}
                onChange={handleCreateFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter agency name"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Agency Code
              </label>
              <input
                type="text"
                name="code"
                value={createFormData.code}
                onChange={handleCreateFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'monospace'
                }}
                placeholder="Enter agency code"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Allowed Statuses
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '8px',
                minHeight: '32px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}>
                {createFormData.statuses.map((status, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {status}
                    <button
                      type="button"
                      onClick={() => removeCreateStatus(status)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        marginLeft: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '0',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={createStatusInput}
                  onChange={(e) => setCreateStatusInput(e.target.value)}
                  onKeyPress={handleCreateStatusKeyPress}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Type status and press Enter to add"
                />
                <button
                  type="button"
                  onClick={addCreateStatus}
                  disabled={!createStatusInput.trim()}
                  style={{
                    backgroundColor: createStatusInput.trim() ? 'var(--primary-color)' : '#ccc',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: createStatusInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Allowed Transaction Types
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '8px',
                minHeight: '32px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa'
              }}>
                {createFormData.transactionTypes.map((transactionType, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {transactionType}
                    <button
                      type="button"
                      onClick={() => removeCreateTransactionType(transactionType)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        marginLeft: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '0',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={createTransactionTypeInput}
                  onChange={(e) => setCreateTransactionTypeInput(e.target.value)}
                  onKeyPress={handleCreateTransactionTypeKeyPress}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Type transaction type and press Enter to add"
                />
                <button
                  type="button"
                  onClick={addCreateTransactionType}
                  disabled={!createTransactionTypeInput.trim()}
                  style={{
                    backgroundColor: createTransactionTypeInput.trim() ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: createTransactionTypeInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseCreateModal}
                disabled={isCreating}
                className="btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleCreateAgency}
                disabled={isCreating || !createFormData.name.trim() || !createFormData.code.trim()}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isCreating || !createFormData.name.trim() || !createFormData.code.trim() ? 0.6 : 1,
                  cursor: isCreating || !createFormData.name.trim() || !createFormData.code.trim() 
                    ? 'not-allowed' 
                    : 'pointer'
                }}
              >
                <span className="material-symbols-outlined small">add</span>
                {isCreating ? 'Creating...' : 'Create Agency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agencies;
